import { Link } from "@tanstack/react-router";
import { MapPin, ArrowUpRight } from "lucide-react";
import { Section } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { nearbyCities, nearbyLabel } from "@/lib/geo-links";
import { RANGPUR_AREAS } from "@/content/rangpur-areas";
import { BN_LOCATION_SLUGS, findBnLocation } from "@/content/bn/locations";

/**
 * "Service coverage" block for a city × service page: the neighbourhoods and
 * districts covered inside the city, plus contextual links to the same service
 * in the nearest cities. Bangla pages only link to cities that actually have a
 * `/bn` counterpart.
 */
export function ServiceCoverage({
  slug,
  city,
  service,
  areas,
  lang = "en",
}: {
  slug: string;
  city: string;
  service: { slug: string; title: string };
  areas: string[];
  lang?: "en" | "bn";
}) {
  const bn = lang === "bn";
  const nearby = nearbyCities(slug, 12)
    .filter((n) => (bn ? BN_LOCATION_SLUGS.has(n.loc.slug) : true))
    .slice(0, 4);

  // Rangpur is the only city with dedicated area pages today.
  const areaPages = slug === "rangpur" ? RANGPUR_AREAS.filter((a) => a.indexable).slice(0, 12) : [];

  return (
    <Section
      eyebrow={bn ? `${city} · সার্ভিস কভারেজ` : `${city} · service coverage`}
      title={
        bn ? (
          <>
            <span className="text-gradient">{city}</span>-এর যেসব এলাকায় {service.title} দিই।
          </>
        ) : (
          <>
            Where I deliver <span className="text-gradient">{service.title} in {city}.</span>
          </>
        )
      }
    >
      <div className="grid lg:grid-cols-2 gap-6">
        <Reveal>
          <div className="rounded-3xl border border-border p-8 bg-card h-full">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
              <MapPin size={12} className="text-primary" />
              {bn ? "শহরের ভেতরের এলাকা" : "Neighbourhoods & districts"}
            </div>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
              {bn
                ? `${city} শহরের নিচের এলাকাগুলোর ব্যবসার জন্য ${service.title} কাজ করি — প্রতিটি এলাকার জন্য আলাদা কীওয়ার্ড, ল্যান্ডিং সেকশন ও Google Business Profile সিগন্যাল সাজানো হয়।`
                : `${service.title} campaigns here are built area by area — separate keyword sets, landing sections, and Google Business Profile signals for each of these parts of ${city}.`}
            </p>
            {areaPages.length > 0 ? (
              <div className="mt-6 flex flex-wrap gap-2">
                {areaPages.map((a) => (
                  <Link
                    key={a.slug}
                    to="/seo-services/rangpur/$area"
                    params={{ area: a.slug }}
                    className="rounded-full border border-border px-3 py-1.5 text-xs hover:border-primary hover:text-primary transition"
                  >
                    {a.name}
                  </Link>
                ))}
              </div>
            ) : (
              <ul className="mt-6 flex flex-wrap gap-2">
                {areas.slice(0, 12).map((a) => (
                  <li
                    key={a}
                    className="rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground"
                  >
                    {a}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.06}>
          <div className="rounded-3xl border border-border p-8 bg-card h-full">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              {bn ? "কাছের শহরগুলোতেও" : "Also covered nearby"}
            </div>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
              {bn
                ? `${city}-এর বাইরেও বিক্রি করেন? পাশের বাজারগুলোর জন্য একই ${service.title} প্লেবুক আলাদা করে লেখা আছে।`
                : `Selling beyond ${city}? The same ${service.title} playbook is written for the closest markets.`}
            </p>
            <div className="mt-6 space-y-2">
              {nearby.map((n) => (
                <Link
                  key={n.loc.slug}
                  to={bn ? "/bn/seo-services/$location/$service" : "/seo-services/$location/$service"}
                  params={{ location: n.loc.slug, service: service.slug }}
                  className="group flex items-center justify-between gap-3 rounded-2xl border border-border px-4 py-3 hover:border-primary transition"
                >
                  <span className="text-sm">
                    {bn
                      ? `${findBnLocation(n.loc.slug)?.cityBn ?? n.loc.city}-এ ${service.title}`
                      : `${service.title} in ${n.loc.city}`}
                    <span className="ml-2 text-xs text-muted-foreground">
                      {bn ? `${n.km} কিমি দূরে` : nearbyLabel(n)}
                    </span>
                  </span>
                  <ArrowUpRight size={14} className="shrink-0 text-muted-foreground group-hover:text-primary" />
                </Link>
              ))}
            </div>
            <div className="mt-6 text-sm text-muted-foreground">
              {bn ? (
                <>
                  <Link to="/bn/seo-services" className="underline hover:text-foreground">
                    সব শহর ও সেবার সূচি
                  </Link>{" "}
                  দেখুন।
                </>
              ) : (
                <>
                  Browse the{" "}
                  <Link to="/seo-services" className="underline hover:text-foreground">
                    full city × service index
                  </Link>
                  .
                </>
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}
